// Lista de documentos subidos por el cliente
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DocumentTextIcon,
  PhotoIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import type { ExpedienteArchivoRead } from '@/types/expediente';

const ESTADO_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pendiente: { label: 'Pendiente de revisión', variant: 'secondary' },
  aprobado: { label: 'Aprobado', variant: 'default' },
  rechazado: { label: 'Rechazado', variant: 'destructive' },
};

function FileIcon({ tipo }: { tipo?: string }) {
  if (tipo?.startsWith('image/')) {
    return <PhotoIcon className="h-5 w-5 text-purple-500" />;
  }
  return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
}

interface Props {
  documents: ExpedienteArchivoRead[];
  onDelete?: (fileId: string) => void;
  isDeleting?: boolean;
}

export function ClienteDocumentList({ documents, onDelete, isDeleting }: Props) {
  if (!documents.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p className="text-sm">Aún no has subido documentos</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {documents.map((doc) => {
        const estadoInfo = ESTADO_BADGE[doc.estado] || { label: doc.estado, variant: 'secondary' as const };
        return (
          <Card key={doc.id}>
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3">
                <FileIcon tipo={doc.tipo} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {doc.nombre}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant={estadoInfo.variant} className="text-xs">
                      {estadoInfo.label}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {new Date(doc.uploaded_at).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                  {doc.estado === 'rechazado' && doc.validation_notes && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Motivo: {doc.validation_notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                  >
                    <a href={doc.url} target="_blank" rel="noopener noreferrer">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </a>
                  </Button>
                  {onDelete && doc.estado !== 'aprobado' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(doc.id)}
                      disabled={isDeleting}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
